import type { Config } from "../config/config.types";
import type { PGSClientConfig } from "../types/PGSClient";

import { getIsEvo } from "./isEvo";

/**
 * Which URL the PGS widget will authenticate against.
 *
 * - `"pgsUrl"` — widget uses `config.PGS_URL`
 * - `"authInitUrl"` — widget uses `config.EVO_DOMAIN`
 */
export type PgsClientTarget = "pgsUrl" | "authInitUrl";

/**
 * Resolves the PGS connection fields (`pgsUrl`, `authInitUrl`, `tenantToken`) from config.
 *
 * `tenantToken` uses `EVO_TENANT` when any of the following is true:
 * - `USE_EVO_ONLY` is set
 * - `target === "authInitUrl"`
 * - `PGS_URL` is an EVO URL
 * Otherwise falls back to `PGS_X_DES_AK_TENANT_TOKEN`.
 *
 * `pgsUrl` is redirected to `EVO_DOMAIN` only when `USE_EVO_ONLY` is set;
 * otherwise `PGS_URL` is always used as-is.
 * `authInitUrl` is always `EVO_DOMAIN`.
 */
const getPgsConnectionConfig = (config: Config, target: PgsClientTarget) => {
  const useEvoCredentials = config.USE_EVO_ONLY || target === "authInitUrl" || getIsEvo(config.PGS_URL);
  const pgsUrl = config.USE_EVO_ONLY ? config.EVO_DOMAIN : config.PGS_URL;
  const tenantToken = useEvoCredentials ? config.EVO_TENANT : config.PGS_X_DES_AK_TENANT_TOKEN;

  return {
    pgsUrl,
    authInitUrl: config.EVO_DOMAIN,
    tenantToken,
  };
};

/**
 * Creates the configuration object for the PGS payment widget.
 * Call-site-specific values (URLs, button props, tokens) are passed via `overrides`.
 */
export const createPgsClientConfig = (
  config: Config,
  target: PgsClientTarget,
  overrides: Omit<PGSClientConfig, "tenantToken" | "pgsUrl" | "authInitUrl">
): PGSClientConfig => {
  const { pgsUrl, authInitUrl, tenantToken } = getPgsConnectionConfig(config, target);

  return {
    tenantToken,
    pgsUrl,
    authInitUrl,
    ...overrides,
  };
};
